const MINUTE_MS = 60_000;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;

export function startOfLocalDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

/** 12-hour clock like `09:30 PM`. */
export function formatClockTime(date: Date, am = 'AM', pm = 'PM'): string {
  let hours = date.getHours();
  const minutes = date.getMinutes();
  const period = hours >= 12 ? pm : am;
  hours = hours % 12;
  if (hours === 0) {
    hours = 12;
  }
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')} ${period}`;
}

/** Absolute date like `Oct 10, 2026`. */
export function formatAbsoluteDate(date: Date, locale = 'en-US'): string {
  return date.toLocaleDateString(locale, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

export function formatWeekday(date: Date, locale = 'en-US'): string {
  return date.toLocaleDateString(locale, { weekday: 'long' });
}

export type RelativeDateTimeBucket =
  | { type: 'justNow' }
  | { type: 'minutesAgo'; minutes: number }
  | { type: 'hoursAgo'; hours: number }
  | { type: 'yesterday'; date: Date }
  | { type: 'absolute'; date: Date };

/**
 * Resolve which relative bucket a timestamp falls into.
 *
 * Rules (in order):
 * - < 1 minute → just now
 * - < 1 hour → minutes ago
 * - < 24 hours → hours ago
 * - calendar yesterday → yesterday + time
 * - earlier than yesterday → absolute date + time
 */
export function resolveRelativeDateTimeBucket(
  input: Date | string | number,
  now: Date = new Date()
): RelativeDateTimeBucket | null {
  const date = input instanceof Date ? input : new Date(input);
  if (Number.isNaN(date.getTime())) {
    return null;
  }

  const diffMs = now.getTime() - date.getTime();
  if (diffMs < MINUTE_MS) {
    return { type: 'justNow' };
  }
  if (diffMs < HOUR_MS) {
    return { type: 'minutesAgo', minutes: Math.max(1, Math.floor(diffMs / MINUTE_MS)) };
  }
  if (diffMs < DAY_MS) {
    return { type: 'hoursAgo', hours: Math.max(1, Math.floor(diffMs / HOUR_MS)) };
  }

  const startToday = startOfLocalDay(now);
  const startYesterday = new Date(startToday);
  startYesterday.setDate(startYesterday.getDate() - 1);

  if (date >= startYesterday && date < startToday) {
    return { type: 'yesterday', date };
  }
  return { type: 'absolute', date };
}

export interface RelativeDateTimeMessages {
  locale: string;
  am: string;
  pm: string;
  justNow: string;
  minutesAgo: (minutes: number) => string;
  hoursAgo: (hours: number) => string;
  yesterday: (time: string) => string;
  absolute: (date: string, time: string) => string;
}

export function formatRelativeDateTime(
  input: Date | string | number | null | undefined,
  messages: RelativeDateTimeMessages,
  now: Date = new Date()
): string {
  if (input == null || input === '') {
    return '';
  }

  const bucket = resolveRelativeDateTimeBucket(input, now);
  if (!bucket) {
    return '';
  }

  switch (bucket.type) {
    case 'justNow':
      return messages.justNow;
    case 'minutesAgo':
      return messages.minutesAgo(bucket.minutes);
    case 'hoursAgo':
      return messages.hoursAgo(bucket.hours);
    case 'yesterday': {
      const time = formatClockTime(bucket.date, messages.am, messages.pm);
      return messages.yesterday(time);
    }
    case 'absolute': {
      const time = formatClockTime(bucket.date, messages.am, messages.pm);
      const dateLabel = formatAbsoluteDate(bucket.date, messages.locale);
      return messages.absolute(dateLabel, time);
    }
  }
}
